/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react';
import { DialogBox, DialogBoxHeader, DialogBoxBody, ButtonPrimary } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import IconButton from 'src/components/icon-button/IconButton';
import { ButtonType } from 'src/types/button-type.enum';
import { IconClose } from '@americanexpress/dls-icons';
import { DLSSize } from 'src/types/dls-size.enum';
import Selectors from 'src/utils/selectors';
import styles from './slider.module.scss';
import CarouselNavigation from 'src/components/atomic/atoms/carouselNavigation/CarouselNavigation';
import colors from 'src/styles/colors.module.scss';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useDeleteUserAttribute } from 'src/hooks/queries/users';
import { UserAttributes } from 'src/types/user-attributes.enum';
import useWalkme from 'src/hooks/useWalkme';
import { WalkmeName } from '../../organisms/walkme';
import { trackClick } from 'src/hooks/useAdobeTags';

interface SliderTags {
    close: string;
    next: string;
}

export interface Slide {
    img: string;
    alt: string;
    title: string;
    mobileContent: JSX.Element;
    nonMobileContent: JSX.Element;
    buttonText: string;
    pathname: string;
    adobeTags: SliderTags;
}

export interface SliderProps {
    slides: Slide[];
    id?: string;
    'data-cy'?: string;
}

interface InternalSliderComponentProps {
    step: number;
    handleClose: () => void;
    handleNext: () => void;
    slides: Slide[];
    id: string;
    'data-cy': string;
    isMinWidthSmall?: boolean;
    closeButtonTitle?: string;
}

function InternalSliderComponent({
    step,
    handleClose,
    handleNext,
    slides,
    id,
    'data-cy': dataCy,
    isMinWidthSmall = false,
    closeButtonTitle = 'close'
}: InternalSliderComponentProps): JSX.Element {
    const noOfSteps = slides.length;
    return (
        step < noOfSteps && (
            <DialogBox
                id={id}
                className={styles.dialogBox}
                aria-label={slides[step].title}
                onClose={handleClose}
                data-cy={dataCy}
            >
                <IconButton
                    variant={ButtonType.PRIMARY_DARK}
                    icon={<IconClose size={DLSSize.MEDIUM} />}
                    title={closeButtonTitle}
                    onClick={handleClose}
                    className={styles.closeButton}
                    data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
                />
                <div className={styles.imageContainer}>
                    <img
                        src={slides[step].img}
                        alt={slides[step].alt}
                        className={step === 2 ? styles.pngImage : styles.svgImage}
                        data-cy={Selectors.SpendSmart.SLIDER_DIALOG_BOX_IMAGE}
                    />
                </div>
                <DialogBoxHeader
                    id="spendsmart-slider-header"
                    className={styles.dialogBoxHeader}
                    data-cy={Selectors.SpendSmart.SLIDER_DIALOG_BOX_HEADER}
                >
                    <Typography variant={TypographyVariants.HEADING_XS} className={colors.brand} component="h2">
                        {slides[step].title}
                    </Typography>
                </DialogBoxHeader>
                <DialogBoxBody
                    id="spendsmart-slider-body"
                    className={styles.dialogBoxBody}
                    data-cy={Selectors.SpendSmart.SLIDER_DIALOG_BOX_BODY}
                >
                    {isMinWidthSmall ? slides[step].nonMobileContent : slides[step].mobileContent}
                </DialogBoxBody>
                <ButtonPrimary
                    size={DLSSize.SMALL}
                    className={styles.nextButton}
                    onClick={handleNext}
                    data-cy={Selectors.SpendSmart.NEXT_BUTTON}
                >
                    {slides[step].buttonText}
                </ButtonPrimary>
                <CarouselNavigation activeIndicator={step} noOfIndicators={noOfSteps} />
            </DialogBox>
        )
    );
}

export default function Slider({
    slides,
    id = 'spendsmart-slider',
    'data-cy': dataCy = Selectors.SpendSmart.SLIDER_DIALOG_BOX
}: SliderProps): JSX.Element {
    const [step, setStep] = useState<number>(0);
    const { mutate: deleteAttribute } = useDeleteUserAttribute();
    const { isMinWidthSmall } = useBreakpoints();
    const { markAsDone: cancelWalkme, isOnOrAfterReleaseDate } = useWalkme(WalkmeName.TRANSACTION_CLASSIFICATION);

    const done = async (): Promise<void> => {
        if (isOnOrAfterReleaseDate) {
            await cancelWalkme();
        }
        deleteAttribute(UserAttributes.IS_NEW_USER);
    };

    const handleNext = async (): Promise<void> => {
        if (step === slides.length - 1) {
            await done();
        } else {
            setStep(step + 1);
        }
        trackClick(slides[step].pathname, slides[step].adobeTags.next);
    };

    const handleClose = async (): Promise<void> => {
        await done();
        trackClick(slides[step].pathname, slides[step].adobeTags.close);
    };

    return (
        <InternalSliderComponent
            step={step}
            handleClose={async () => await handleClose()}
            handleNext={async () => await handleNext()}
            slides={slides}
            isMinWidthSmall={isMinWidthSmall}
            id={id}
            data-cy={dataCy}
        />
    );
}
